import React from 'react';
import PropTypes from 'prop-types';
import { tz } from 'moment-timezone';
import { sortBy } from 'lodash';

import DataCard from '../../../common/card/DataCard';

import { ACTIVITY_LOG_DELIVERY_STATUSES, ORDER_STATUSES } from '../../../../utils/constants';
import { getProcessingEndLog } from '../../../../utils/getProcessingEndLog';

const DATE_FORMAT = 'MM/DD/YY, hh:mma z';

export const OrderTimeline = ({ data }) => {
  const { activityLog = [], isProcessedAtHub, orderType, rack } = data;

  const LABEL_BY_STATUS = {
    ...(activityLog.some(({ status }) => status === ORDER_STATUSES.READY_FOR_PROCESSING) && {
      [ORDER_STATUSES.READY_FOR_PROCESSING]: 'Intake',
    }),
    [ORDER_STATUSES.DESIGNATED_FOR_PROCESSING_AT_HUB]: 'Intake',
    [ORDER_STATUSES.PROCESSING]: 'Processing',
    [ORDER_STATUSES.DROPPED_OFF_AT_HUB]: 'Dropoff by driver at Hub',
    [ORDER_STATUSES.IN_TRANSIT_TO_HUB]: 'Pickup by driver at Store',
    [ORDER_STATUSES.RECEIVED_AT_HUB_FOR_PROCESSING]: 'Receive at Hub',
    ...(isProcessedAtHub && { [[ORDER_STATUSES.READY_FOR_PICKUP]]: 'Receive at store' }),
    [ORDER_STATUSES.COMPLETED]: 'Pickup & Complete',
    [ORDER_STATUSES.CANCELLED]: 'Cancel',
    [ORDER_STATUSES.EN_ROUTE_TO_CUSTOMER]: 'En route to customer, with driver',
    [ORDER_STATUSES.READY_FOR_DRIVER_PICKUP]: 'Ready for driver pickup',
    [ORDER_STATUSES.HUB_PROCESSING_ORDER]: 'Processing',
    [ORDER_STATUSES.IN_TRANSIT_TO_STORE]: 'Pickup by driver at Hub',
    [ORDER_STATUSES.READY_FOR_INTAKE]: 'Ready for intake at store',
    ...(orderType === 'ONLINE' && { [ORDER_STATUSES.SUBMITTED]: 'Submitted Online' }),
    [ORDER_STATUSES.DROPPED_OFF_AT_STORE]: 'Dropoff by driver at Store',
    [ORDER_STATUSES.PAYMENT_REQUIRED]: 'Balance due',
    // Delivery-level events; labels match Business Manager's timeline.
    [ACTIVITY_LOG_DELIVERY_STATUSES.PICKUP_RESCHEDULED]: 'Pickup Rescheduled',
    [ACTIVITY_LOG_DELIVERY_STATUSES.DELIVERY_RESCHEDULED]: 'Delivery Rescheduled',
    [ACTIVITY_LOG_DELIVERY_STATUSES.DELIVERY_CANCELED]: 'Delivery Canceled',
    [ACTIVITY_LOG_DELIVERY_STATUSES.MISSED_PICKUP]: 'Missed Pickup',
  };

  // Sorted before filtering: HUB_PROCESSING_COMPLETE closes a window but has no label, so it
  // would not survive the filter below.
  const sortedActivityLog = sortBy(activityLog, ['id']);

  const filteredLog = sortedActivityLog.filter(({ status }) => LABEL_BY_STATUS[status]);

  const details = filteredLog.map((log) => {
    const { employeeName, isAdjusted, paymentValidationOverridden, status, updatedAt } = log;
    const timezone = tz.guess();
    let timeString = tz(updatedAt, timezone).format(DATE_FORMAT);

    if ([ORDER_STATUSES.HUB_PROCESSING_ORDER, ORDER_STATUSES.PROCESSING].includes(status)) {
      const endStatus =
        status === ORDER_STATUSES.PROCESSING
          ? ORDER_STATUSES.READY_FOR_PICKUP
          : ORDER_STATUSES.HUB_PROCESSING_COMPLETE;
      const endLog = getProcessingEndLog({ sortedActivityLog, startLog: log, endStatus });
      const endString = endLog ? tz(endLog.updatedAt, timezone).format(DATE_FORMAT) : '';

      timeString = `${tz(updatedAt, timezone).format(DATE_FORMAT)} -\n${endString}`;
    }

    if (
      rack &&
      ((isProcessedAtHub && status === ORDER_STATUSES.READY_FOR_PICKUP) ||
        (!isProcessedAtHub && status === ORDER_STATUSES.PROCESSING) ||
        (orderType === 'RESIDENTIAL' && status === ORDER_STATUSES.HUB_PROCESSING_ORDER))
    ) {
      timeString = `${timeString} (Rack #${rack})`;
    }

    return {
      label: isAdjusted ? 'Adjust Order' : LABEL_BY_STATUS[status],
      data: employeeName ? `${timeString} ${employeeName}` : timeString,
      paymentValidationOverridden,
    };
  });

  return <DataCard title="Order Timeline" cardData={details} />;
};

OrderTimeline.propTypes = {
  data: PropTypes.shape({
    activityLog: PropTypes.arrayOf(
      PropTypes.shape({
        status: PropTypes.string,
        updatedAt: PropTypes.string,
        isAdjusted: PropTypes.bool,
        employeeName: PropTypes.string,
      }),
    ),
    isProcessedAtHub: PropTypes.bool,
    orderType: PropTypes.string,
    rack: PropTypes.string,
  }).isRequired,
};
